import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatCountryOption } from "@/lib/countryLabel";
import api, { formatError } from "@/lib/api";
import { track } from "@/lib/analytics";

const homeFor = (role) =>
  role === "staff" ? "/me" : role === "salesperson" ? "/sales" : role === "super_admin" ? "/platform" : role === "sales_applicant" ? "/onboarding" : "/dashboard";

export const AuthModal = ({ open, onClose, initialMode = "login", lockMode = false, portal = null, onApply }) => {
  const partner = portal === "partner";
  const { login, register } = useAuth();
  const { lang, dict } = useLang();
  const tr = (s) => (!s || lang === "en" ? s : (dict[s] ?? s));
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ name: "", email: "", password: "", restaurant: "", restaurant_type: "", country_code: "GB", preferred_language: "en" });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({ countries: [], languages: [] });

  useEffect(() => {
    // Carry the visitor's Hero language choice into account creation as their initial interface language.
    if (open) { setMode(initialMode); setBusy(false); setForm((f) => ({ ...f, preferred_language: lang || "en" })); }
  }, [open, initialMode, lang]);

  useEffect(() => {
    if (open && mode === "register" && !opts.countries.length) {
      api.get("/public/signup-options").then((r) => setOpts(r.data)).catch(() => {});
    }
  }, [open, mode, opts.countries.length]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const fill = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !agree) { toast.error("Please accept the Terms of Service and Privacy Policy to continue."); return; }
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message || "If an account exists, a reset link has been sent.");
        setMode("login");
      } else {
        const u = mode === "login"
          ? await login(form.email, form.password, portal)
          : await register({ name: form.name, email: form.email, password: form.password, restaurant: form.restaurant || "My Restaurant", restaurant_type: form.restaurant_type || null, country_code: form.country_code || "GB", preferred_language: form.preferred_language || "en", accepted_legal: agree });
        toast.success(mode === "login" ? "Welcome back" : "Account created");
        if (mode === "register") track("sign_up", { method: "email" });
        else track("login", { method: "email" });
        navigate(homeFor(u.role), { replace: true });
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (portal) sessionStorage.setItem("si_login_portal", portal);
    const redirectUrl = window.location.origin + "/me";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const title = partner && mode === "login" ? "Sales Partner sign in" : mode === "login" ? "Sign in" : mode === "register" ? "Create your account" : "Reset your password";
  const subtitle = partner && mode === "login" ? "Access your Sales Partner portal." : mode === "login" ? "Access your restaurant dashboard." : mode === "register" ? "Set up your restaurant in minutes." : "Enter your email and we'll send you a secure reset link.";
  const cta = busy ? "Please wait..." : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send reset link";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="auth-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} data-testid="auth-modal-backdrop" />
      <div className="relative w-full max-w-md lux-card p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
        style={{ animation: "fadeUp 0.4s cubic-bezier(0.22,1,0.36,1) both" }}>
        <button onClick={onClose} data-testid="auth-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors duration-200">
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-serif-lux text-3xl sm:text-4xl font-light">{title}</h2>
        <p className="text-white/50 mt-1.5 mb-6 text-sm">{subtitle}</p>

        {mode === "register" && (
          <div className="-mt-4 mb-6 flex items-center gap-1.5 text-xs text-[#D4AF37]" data-testid="auth-plan-summary">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="font-medium"><span data-no-translate className="font-bold">£350/month</span><span data-no-translate> · </span>unlimited staff · ARI intelligence included</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <input data-testid="register-name-input" required placeholder={tr("Your name")} value={form.name} onChange={fill("name")} className="lux-input w-full rounded-lg px-4 py-3 text-sm" />
              <input data-testid="register-restaurant-input" placeholder={tr("Restaurant name")} value={form.restaurant} onChange={fill("restaurant")} className="lux-input w-full rounded-lg px-4 py-3 text-sm" />
              <select data-testid="register-type-select" value={form.restaurant_type} onChange={fill("restaurant_type")} className="lux-input w-full rounded-lg px-4 py-3 text-sm">
                <option value="">{tr("Venue type (builds your Academy)…")}</option>
                {["Fine Dining", "Casual Dining", "Café", "Pub", "Hotel Restaurant", "Fast Food / QSR", "Bakery", "Cocktail Bar", "Wine Bar", "Buffet", "Steakhouse", "Seafood Restaurant", "Pizzeria", "Asian Restaurant", "Middle Eastern Restaurant", "Other Hospitality Venue"].map((t) => <option key={t} value={t}>{tr(t)}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Country</label>
                  <select data-testid="register-country-select" data-no-translate value={form.country_code} onChange={fill("country_code")} className="lux-input w-full rounded-lg px-4 py-3 text-sm">
                    {(opts.countries.length ? opts.countries : [{ code: "GB", name: "United Kingdom", verified: true }]).map((c) => <option key={c.code} value={c.code}>{formatCountryOption(c, dict)}</option>)}
                  </select>
                  <p className="text-[10px] text-white/30 mt-1">Country sets currency & payroll (UK live; others coming soon)</p>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-1">Language</label>
                  <select data-testid="register-language-select" value={form.preferred_language} onChange={fill("preferred_language")} className="lux-input w-full rounded-lg px-4 py-3 text-sm">
                    {(opts.languages.length ? opts.languages : [{ code: "en", name: "English" }]).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
                  </select>
                  <p className="text-[10px] text-white/30 mt-1">Platform & communications</p>
                </div>
              </div>
            </>
          )}
          <input data-testid="login-email-input" required type="email" placeholder={tr("Email")} value={form.email} onChange={fill("email")} className="lux-input w-full rounded-lg px-4 py-3 text-sm" />
          {mode !== "forgot" && (
            <input data-testid="login-password-input" required type="password" placeholder={tr("Password")} value={form.password} onChange={fill("password")} className="lux-input w-full rounded-lg px-4 py-3 text-sm" />
          )}
          {mode === "login" && (
            <div className="text-right">
              <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="text-xs text-white/50 hover:text-[#D4AF37] transition-colors">
                Forgot password?
              </button>
            </div>
          )}
          {mode === "register" && (
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="register-legal-agree-label">
              <input type="checkbox" data-testid="register-legal-checkbox" checked={agree}
                onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#D4AF37] shrink-0" />
              <span className="text-xs text-white/50 leading-relaxed">
                I agree to the{" "}
                <a href="/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">Terms of Service</a>
                {" "}and{" "}
                <a href="/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">Privacy Policy</a>.
              </span>
            </label>
          )}
          <button data-testid="login-submit-button" disabled={busy} type="submit" className="gold-btn w-full rounded-lg py-3 text-sm">
            {cta}
          </button>
        </form>

        {mode !== "forgot" && !partner && (
          <>
            <div className="flex items-center gap-4 my-6">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-white/30 uppercase tracking-widest">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <button data-testid="google-login-button" onClick={googleLogin} className="w-full rounded-lg py-3 text-sm border border-white/15 bg-[#1A1A1D] hover:border-white/30 transition-colors flex items-center justify-center gap-3">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
              Continue with Google
            </button>
          </>
        )}

        {partner && (
          <p className="mt-7 text-center text-sm text-white/50" data-testid="partner-auth-footer">
            {mode === "forgot" ? (
              <button data-testid="back-to-login" onClick={() => setMode("login")} className="text-[#D4AF37] hover:underline">Back to sign in</button>
            ) : (
              <>Don't have a partner account?{" "}
                <button data-testid="partner-apply-link" onClick={() => { onClose(); onApply && onApply(); }} className="text-[#D4AF37] hover:underline">Apply now</button>
              </>
            )}
          </p>
        )}
        {!lockMode && !partner && (
        <p className="mt-7 text-center text-sm text-white/50">
          {mode === "forgot" ? (
            <button data-testid="back-to-login" onClick={() => setMode("login")} className="text-[#D4AF37] hover:underline">Back to sign in</button>
          ) : (
            <>
              {mode === "login" ? "New to Admin Restaurant Intelligence? " : "Already have an account? "}
              <button data-testid="toggle-auth-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#D4AF37] hover:underline">
                {mode === "login" ? "Create an account" : "Sign in"}
              </button>
            </>
          )}
        </p>
        )}
        {lockMode && (
          <p className="mt-7 text-center text-sm text-white/50">
            Already have an account?{" "}
            <a href="/" data-testid="signin-on-landing" className="text-[#D4AF37] hover:underline">Sign in from the homepage</a>
          </p>
        )}
      </div>
    </div>
  );
};
